import { Autocomplete, debounce, FormControl, FormLabel, TextField } from '@mui/material';
import { FC, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { TicketEntryHaulerDto } from '../../dtos';
import { useGetOrderReviewHaulersQuery } from '../../store/generated/generatedApi';
import { format } from 'date-fns';

export interface IHaulerSelectProps {
    selectedHauler: TicketEntryHaulerDto | null;
    handleSelectedHaulerChange: (selectedHauler?: TicketEntryHaulerDto) => void;
    required?: boolean;
    date?: Date;
    disabled?: boolean;
}

export const HaulerSelect: FC<IHaulerSelectProps> = props => {
    const { selectedHauler, handleSelectedHaulerChange, required, date, disabled } = props;
    const [inputValue, setInputValue] = useState('');

    const { data: haulers, isLoading: isLoadingHaulers } = useGetOrderReviewHaulersQuery({
        orderDate: date ? format(date, "yyyy-MM-dd") : undefined,
        searchString: inputValue
    });

    const debouncedInputChange = useMemo(
        () => debounce((value: string) => {
            setInputValue(value);
        }, 300)
        , [setInputValue]);

    const handleSelectChange = useCallback((_: SyntheticEvent<Element, Event>, value: TicketEntryHaulerDto | null) => {
        handleSelectedHaulerChange(value ?? undefined);
        setInputValue('');
    }, [handleSelectedHaulerChange]);

    const getSelectedHauler = useCallback(() => {        
        if (selectedHauler === null) {
            return null;
        }
        
        const hauler = haulers?.find(x => (x.driverId === selectedHauler.driverId) && 
            (x.brokerId === selectedHauler.brokerId) && 
            (x.equipmentId === selectedHauler.equipmentId));

        return hauler ?? null;
    }, [haulers, selectedHauler]);

    return (
        <FormControl fullWidth required={required} disabled={disabled}>
            <FormLabel>Hauler</FormLabel>
            <Autocomplete
                onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                        debouncedInputChange(value)
                    }
                }}
                value={getSelectedHauler()}
                onChange={handleSelectChange}
                loading={isLoadingHaulers}
                options={haulers ?? []}
                getOptionLabel={x => x.name}
                renderInput={(params) => (
                    <TextField
                        {...params}
                    />
                )}
                disabled={disabled}
            />
        </FormControl>
    )
}